import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  setDoc,
  arrayUnion
} from "firebase/firestore";

const normalizeMonthlyRecurrence = (recurrence) => ({
  frequency: "monthly",
  interval: Math.max(1, Number(recurrence?.interval) || 1),
  createTiming: "periodEnd"
});

let tenantScopeId = "";

export const configureMonthlyProcessTenantScope = (tenantId = "") => {
  tenantScopeId = String(tenantId || "").trim();
};

const scopedCollection = (collectionName) => tenantScopeId
  ? collection(db, "tenants", tenantScopeId, collectionName)
  : collection(db, collectionName);

const scopedDoc = (collectionName, documentId) => tenantScopeId
  ? doc(db, "tenants", tenantScopeId, collectionName, documentId)
  : doc(db, collectionName, documentId);

const tenantProcessFields = (processProjectId) => tenantScopeId ? {
  tenantId: tenantScopeId,
  boardId: processProjectId,
  createdBy: auth.currentUser?.uid || "",
  createdByEmail: auth.currentUser?.email || ""
} : {};

export const DEFAULT_PROCESS_PROJECT_ID = "monthly-processes";

export const ensureDefaultProcessProject = async () => {
  await setDoc(scopedDoc("processProjects", DEFAULT_PROCESS_PROJECT_ID), {
    name: "Aylık Süreçler",
    slug: DEFAULT_PROCESS_PROJECT_ID,
    type: "kanban",
    isActive: true,
    order: 1,
    isSystemDefault: true,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const getProcessProjects = async () => {
  try {
    await ensureDefaultProcessProject();
  } catch (error) {
    // Görüntüleyici kullanıcıların süreç projelerini listeleyebilmesi için
    // varsayılan proje oluşturma/güncelleme yetkisi yoksa listelemeye devam edilir.
  }

  const q = query(scopedCollection("processProjects"), orderBy("order", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) => item.isActive !== false);
};

export const createProcessProject = async ({ name, createdByUid = "", createdByEmail = "" }) => {
  const safeName = String(name || "").trim();

  if (!safeName) {
    throw new Error("Süreç projesi adı zorunludur.");
  }

  return addDoc(scopedCollection("processProjects"), {
    name: safeName,
    slug: safeName
      .toLocaleLowerCase("tr")
      .replace(/ı/g, "i")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, ""),
    type: "kanban",
    isActive: true,
    order: Date.now(),
    createdByUid,
    createdByEmail,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProcessProject = async ({ projectId, data }) => {
  if (!projectId) {
    throw new Error("Süreç projesi ID bulunamadı.");
  }

  return updateDoc(scopedDoc("processProjects", projectId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const getProcessTemplates = async (processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  const q = query(scopedCollection("processTemplates"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) =>
      processProjectId === DEFAULT_PROCESS_PROJECT_ID
        ? !item.processProjectId || item.processProjectId === processProjectId
        : item.processProjectId === processProjectId
    );
};

export const createProcessTemplate = async ({
  name,
  steps,
  recurrence,
  processProjectId = DEFAULT_PROCESS_PROJECT_ID
}) => {
  return addDoc(scopedCollection("processTemplates"), {
    name,
    steps,
    recurrence: normalizeMonthlyRecurrence(recurrence),
    processProjectId,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProcessTemplate = async ({ templateId, name, steps, recurrence }) => {
  if (!templateId) {
    throw new Error("Şablon ID bulunamadı.");
  }

  return updateDoc(scopedDoc("processTemplates", templateId), {
    name,
    steps,
    recurrence: normalizeMonthlyRecurrence(recurrence),
    updatedAt: serverTimestamp()
  });
};

export const deleteProcessTemplate = async (templateId) => {
  if (!templateId) {
    throw new Error("Şablon ID bulunamadı.");
  }

  return deleteDoc(scopedDoc("processTemplates", templateId));
};

export const getMonthlyProcesses = async (processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  const q = query(scopedCollection("monthlyProcesses"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) =>
      tenantScopeId
        ? (item.processProjectId || item.boardId) === processProjectId
        : processProjectId === DEFAULT_PROCESS_PROJECT_ID
        ? !item.processProjectId || item.processProjectId === processProjectId
        : item.processProjectId === processProjectId
    );
};

export const getAllMonthlyProcesses = async () => {
  const q = query(scopedCollection("monthlyProcesses"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createMonthlyProcess = async ({
  companyName,
  companyShortName = "",
  title = "",
  month,
  scheduleType = "month",
  startDate = "",
  endDate = "",
  template,
  owner,
  processProjectId = DEFAULT_PROCESS_PROJECT_ID,
  note = "",
  source = "",
  sourceId = "",
  priority = "",
  dueDate = "",
  extraFields = {}
}) => {
  const steps = (template.steps || []).map((step, index) => ({
    order: step.order || index + 1,
    groupTitle: step.groupTitle || "",
    title: step.title,
    isRequired: step.isRequired !== false,
    completed: false,
    completedAt: null,
    completedBy: ""
  }));

  return addDoc(scopedCollection("monthlyProcesses"), {
    companyName: companyName || "",
    companyShortName: companyShortName || "",
    title: title || template.name || "",
    month: month || "",
    scheduleType: scheduleType || "month",
    startDate: startDate || "",
    endDate: endDate || startDate || "",
    owner: owner || "Atanmamış",
    autoRepeat: template.autoRepeatDefault || false,
    recurrence: normalizeMonthlyRecurrence(template.recurrence),
    note: note || "",
    notes: [],
    source: source || "",
    sourceId: sourceId || "",
    priority: priority || "",
    dueDate: dueDate || "",
    templateId: template.id,
    templateName: template.name,
    processProjectId,
    ...tenantProcessFields(processProjectId),
    status: "active",
    completionRate: 0,
    steps,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...extraFields
  });
};

export const updateMonthlyProcessSteps = async ({
  processId,
  steps,
  userEmail,
  status
}) => {
  const completedCount = steps.filter((step) => step.completed).length;
  const completionRate = steps.length ? Math.round((completedCount / steps.length) * 100) : 0;
  const stepProgress = steps.reduce((acc, step, index) => {
    acc[String(index)] = {
      completed: Boolean(step.completed),
      completedAt: step.completedAt || null,
      completedBy: step.completedBy || "",
      verification: step.verification || null
    };
    return acc;
  }, {});

  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    steps,
    stepProgress,
    completionRate,
    status: status || "active",
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessDetails = async ({
  processId,
  data,
  userEmail = ""
}) => {
  if (!processId) {
    throw new Error("Görev ID bulunamadı.");
  }

  return updateDoc(scopedDoc("monthlyProcesses", processId), {
    ...data,
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessStepProgress = async ({
  processId,
  steps,
  userEmail,
  status,
  extraFields = {}
}) => {
  const completedCount = steps.filter((step) => step.completed).length;
  const completionRate = steps.length ? Math.round((completedCount / steps.length) * 100) : 0;
  const stepProgress = steps.reduce((acc, step, index) => {
    acc[String(index)] = {
      completed: Boolean(step.completed),
      completedAt: step.completedAt || null,
      completedBy: step.completedBy || "",
      verification: step.verification || null
    };

    return acc;
  }, {});

  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    stepProgress,
    completionRate,
    status: status || "active",
    ...extraFields,
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessExemptionDayReportStatus = async ({
  processId,
  delivered,
  userEmail
}) => {
  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    exemptionDayReportDelivered: Boolean(delivered),
    exemptionDayReportAsked: true,
    exemptionDayReportUpdatedAt: serverTimestamp(),
    exemptionDayReportUpdatedBy: userEmail || "",
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const recordMonthlyProcessReminderDraft = async ({
  processId,
  channel = "draft",
  subject = "",
  to = "",
  cc = [],
  phone = "",
  missingSteps = [],
  userEmail
}) => {
  const reminderRecord = {
    channel,
    subject,
    to,
    cc,
    phone,
    missingSteps,
    createdAt: new Date().toISOString(),
    createdBy: userEmail || ""
  };

  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    reminderDrafts: arrayUnion(reminderRecord),
    lastReminderDraftAt: serverTimestamp(),
    lastReminderDraftBy: userEmail || "",
    lastReminderDraftChannel: channel,
    lastReminderDraftSubject: subject,
    lastReminderDraftTo: to,
    lastReminderDraftCc: cc,
    lastReminderDraftPhone: phone,
    lastReminderDraftMissingSteps: missingSteps,
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessCompletion = async ({
  processId,
  steps,
  userEmail,
  status
}) => {
  if (status === "completed") {
    return completeMonthlyProcess({ processId, userEmail });
  }

  return updateMonthlyProcessStepProgress({
    processId,
    steps,
    userEmail,
    status
  });
};

export const completeMonthlyProcess = async ({ processId, userEmail }) => {
  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    status: "completed",
    owner: "TAMAMLANDI",
    completedAt: serverTimestamp(),
    completedBy: userEmail || "",
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });

  return { success: true };
};

export const archiveMonthlyProcess = async (processId) => {
  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    status: "archived",
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteMonthlyProcess = async (processId) => {
  await deleteDoc(scopedDoc("monthlyProcesses", processId));
};

export const updateMonthlyProcessRepeatSettings = async ({
  processId,
  autoRepeat,
  recurrence,
  userEmail
}) => {
  if (!processId) {
    throw new Error("Süreç ID bulunamadı.");
  }

  return updateDoc(scopedDoc("monthlyProcesses", processId), {
    autoRepeat: Boolean(autoRepeat),
    recurrence: normalizeMonthlyRecurrence(recurrence),
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const getProcessOwners = async (processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  const q = query(scopedCollection("processOwners"), orderBy("order", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) =>
      processProjectId === DEFAULT_PROCESS_PROJECT_ID
        ? !item.processProjectId || item.processProjectId === processProjectId
        : item.processProjectId === processProjectId
    );
};

export const createProcessOwner = async (
  name,
  processProjectId = DEFAULT_PROCESS_PROJECT_ID,
  options = {}
) => {
  return addDoc(scopedCollection("processOwners"), {
    name,
    processProjectId,
    ...(options.type ? { type: options.type } : {}),
    isActive: true,
    order: Number.isFinite(Number(options.order)) ? Number(options.order) : Date.now(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProcessOwner = async ({ ownerId, data }) => {
  return updateDoc(scopedDoc("processOwners", ownerId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteProcessOwner = async (ownerId) => {
  return deleteDoc(scopedDoc("processOwners", ownerId));
};

export const updateMonthlyProcessOwner = async ({ processId, owner }) => {
  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    owner,
    updatedAt: serverTimestamp()
  });

  return { success: true };
};

export const getMonthlyProcessTimeEntries = async (processId) => {
  if (!processId) return [];

  const q = query(
    scopedCollection("processTimeEntries"),
    where("processId", "==", processId)
  );
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .sort((first, second) =>
      String(second.startedAtIso || "").localeCompare(String(first.startedAtIso || ""))
    );
};

export const startMonthlyProcessTimer = async ({
  processId,
  processTitle = "",
  userId = "",
  userEmail = "",
  userName = ""
}) => {
  if (!processId) {
    throw new Error("Görev ID bulunamadı.");
  }

  const nowIso = new Date().toISOString();

  const reference = await addDoc(scopedCollection("processTimeEntries"), {
    processId,
    processTitle,
    userId,
    userEmail,
    userName,
    entryType: "timer",
    status: "running",
    isManual: false,
    startedAtIso: nowIso,
    endedAtIso: "",
    durationSeconds: 0,
    note: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return { id: reference.id, startedAtIso: nowIso };
};

export const stopMonthlyProcessTimer = async ({
  entryId,
  startedAtIso,
  userEmail = ""
}) => {
  if (!entryId) {
    throw new Error("Zaman kaydı bulunamadı.");
  }

  const endDate = new Date();
  const startDate = new Date(startedAtIso);
  const durationSeconds = Number.isNaN(startDate.getTime())
    ? 0
    : Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 1000));

  await updateDoc(scopedDoc("processTimeEntries", entryId), {
    status: "stopped",
    endedAtIso: endDate.toISOString(),
    durationSeconds,
    stoppedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });

  return { durationSeconds };
};

export const addManualMonthlyProcessTimeEntry = async ({
  processId,
  processTitle = "",
  userId = "",
  userEmail = "",
  userName = "",
  startedAtIso,
  endedAtIso,
  durationSeconds,
  note = ""
}) => {
  if (!processId) {
    throw new Error("Görev ID bulunamadı.");
  }

  return addDoc(scopedCollection("processTimeEntries"), {
    processId,
    processTitle,
    userId,
    userEmail,
    userName,
    entryType: "manual",
    status: "completed",
    isManual: true,
    startedAtIso,
    endedAtIso,
    durationSeconds,
    note,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const getCompanyContacts = async () => {
  const q = query(scopedCollection("companyContacts"), orderBy("companyName", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createCompanyContact = async ({
  companyId,
  branchId,
  companyName,
  companyShortName,
  contactName,
  title,
  phone,
  email,
  note
}) => {
  return addDoc(scopedCollection("companyContacts"), {
    companyId: companyId || "",
    branchId: branchId || "",
    companyName,
    companyShortName: companyShortName || "",
    contactName,
    title: title || "",
    phone: phone || "",
    email: email || "",
    note: note || "",
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteCompanyContact = async (contactId) => {
  return deleteDoc(scopedDoc("companyContacts", contactId));
};

export const updateCompanyContact = async ({ contactId, data }) => {
  return updateDoc(scopedDoc("companyContacts", contactId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const getTechnoparkContacts = async () => {
  const q = query(scopedCollection("technoparkContacts"), orderBy("technoparkName", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createTechnoparkContact = async ({
  technoparkName,
  contactName,
  title,
  phone,
  email,
  note,
  notes
}) => {
  const safeNotes = Array.isArray(notes)
    ? notes.map((item) => String(item || "").trim()).filter(Boolean)
    : (note ? [String(note).trim()] : []);

  return addDoc(scopedCollection("technoparkContacts"), {
    technoparkName,
    contactName,
    title: title || "",
    phone: phone || "",
    email: email || "",
    note: note || "",
    notes: safeNotes,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateTechnoparkContact = async ({ contactId, data }) => {
  return updateDoc(scopedDoc("technoparkContacts", contactId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteTechnoparkContact = async (contactId) => {
  return deleteDoc(scopedDoc("technoparkContacts", contactId));
};

export const getTechnoparkNotes = async () => {
  const q = query(scopedCollection("technoparkNotes"), orderBy("technoparkName", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createTechnoparkNote = async ({
  technoparkName,
  note
}) => {
  return addDoc(scopedCollection("technoparkNotes"), {
    technoparkName,
    note: String(note || "").trim(),
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteTechnoparkNote = async (noteId) => {
  return deleteDoc(scopedDoc("technoparkNotes", noteId));
};

export const createMonthlyProcessesBulk = async ({
  companies,
  month,
  template,
  owner,
  existingProcesses = [],
  processProjectId = DEFAULT_PROCESS_PROJECT_ID
}) => {
  const uniqueCompanies = [...new Set((companies || []).filter(Boolean))];
  const skippedCompanies = [];
  const createdCompanies = [];

  for (const companyName of uniqueCompanies) {
    const alreadyExists = existingProcesses.some(
      (process) =>
        process.companyName === companyName &&
        process.month === month &&
        process.templateId === template.id
    );

    if (alreadyExists) {
      skippedCompanies.push(companyName);
      continue;
    }

    await createMonthlyProcess({
      companyName,
      month,
      template,
      owner,
      processProjectId
    });

    createdCompanies.push(companyName);
  }

  return {
    createdCount: createdCompanies.length,
    skippedCount: skippedCompanies.length
  };
};

export const updateMonthlyProcessNote = async ({ processId, notes }) => {
  await updateDoc(scopedDoc("monthlyProcesses", processId), {
    notes: notes || [],
    note: "",
    updatedAt: serverTimestamp()
  });

  return { success: true };
};
